/**
 * Settings Actions Component
 *
 * Provides save, reset, and reset to defaults actions for settings.
 *
 * @since 1.0.0
 */

import { ArrowPathIcon, ArrowUturnLeftIcon, CheckIcon } from '@heroicons/react/24/outline';
import { colors } from '@redfoxwp/core-components/colors';
import { Button } from '@redfoxwp/core-components/components/button';
import { Card } from '@redfoxwp/core-components/components/card';
import { Flex } from '@redfoxwp/core-components/components/flex';
import { FlexItem } from '@redfoxwp/core-components/components/flex-item';
import { Modal } from '@redfoxwp/core-components/components/modal';
import { __ } from '@wordpress/i18n';

import { useSettingsContext } from '../../contexts/settings-context';
import { useValidationAwareSave } from '../../hooks/use-validation-aware-save';

/**
 * Settings Actions component
 *
 * @since 1.0.0
 *
 * @return JSX element.
 */
export const SettingsActions = (): JSX.Element => {
  const { hasChanges, isSaving, isResetting, resetSettings, resetToDefaults } =
    useSettingsContext();

  const { saveSettings, validationErrors, showValidationModal, closeValidationModal } =
    useValidationAwareSave();

  const handleSave = async () => {
    try {
      await saveSettings();
    } catch {
      // Error handling is managed by the context
    }
  };

  const handleResetToDefaults = async () => {
    try {
      await resetToDefaults();
    } catch {
      // Error handling is managed by the context
    }
  };

  const isDisabled = isSaving || isResetting;

  return (
    <>
      <Card styleProps={{ padding: '30px', radius: '10px', background: colors?.white }}>
        <Flex justify='space-between' align='center'>
          <FlexItem>
            <Button
              variant='outline'
              onClick={handleResetToDefaults}
              disabled={isDisabled}
              loading={isResetting}
              size='small'
              styleProps={{
                borderColor: colors?.gray?.[300],
                color: colors?.gray?.[500],
                hoverBackgroundColor: colors?.gray?.[100],
                hoverColor: colors?.gray?.[700],
                hoverBorderColor: colors?.gray?.[300],
              }}
            >
              <ArrowPathIcon style={{ width: '16px', height: '16px', marginRight: '8px' }} />
              {isResetting
                ? __('Resetting...', 'redfox-added-to-cart-popup')
                : __('Reset to Defaults', 'redfox-added-to-cart-popup')}
            </Button>
          </FlexItem>
          <Flex gap='15px'>
            <Button
              variant='outline'
              onClick={resetSettings}
              disabled={!hasChanges || isDisabled}
              size='medium'
            >
              <ArrowUturnLeftIcon style={{ width: '16px', height: '16px', marginRight: '8px' }} />
              {__('Reset Changes', 'redfox-added-to-cart-popup')}
            </Button>
            <Button
              variant='solid'
              onClick={handleSave}
              disabled={!hasChanges || isDisabled}
              loading={isSaving}
              size='medium'
            >
              <CheckIcon style={{ width: '16px', height: '16px', marginRight: '8px' }} />
              {isSaving
                ? __('Saving...', 'redfox-added-to-cart-popup')
                : __('Save Changes', 'redfox-added-to-cart-popup')}
            </Button>
          </Flex>
        </Flex>
      </Card>

      {/* Global Validation Error Modal */}
      {showValidationModal && (
        <Modal
          isOpen={showValidationModal}
          onClose={closeValidationModal}
          title={__('Validation Errors', 'redfox-added-to-cart-popup')}
          strings={{
            closeModal: __('Close', 'redfox-added-to-cart-popup'),
          }}
        >
          <div style={{ marginBottom: '20px' }}>
            <p>
              {__(
                'Your settings contain validation errors and cannot be saved. Please fix the following errors:',
                'redfox-added-to-cart-popup'
              )}
            </p>
            {validationErrors.map((validationError) => (
              <div key={validationError.componentKey} style={{ marginTop: '20px' }}>
                <h4 style={{ marginBottom: '10px', fontSize: '16px', fontWeight: '600' }}>
                  {validationError.componentKey === 'advanced-css'
                    ? __('Custom CSS', 'redfox-added-to-cart-popup')
                    : validationError.componentKey}
                </h4>
                <ul style={{ paddingLeft: '20px' }}>
                  {validationError.errors.map((error, index) => (
                    <li key={index} style={{ marginBottom: '8px' }}>
                      <strong>
                        {__('Line', 'redfox-added-to-cart-popup')} {error.line}:
                      </strong>{' '}
                      {error.message}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <Button variant='solid' onClick={closeValidationModal}>
              {__('Close', 'redfox-added-to-cart-popup')}
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};
